import type { ComponentType } from 'react';
import type { RendererProps } from '../../registry.js';
import { RowView } from '../../visualizations/Row/RowView';
import { ColumnView } from '../../visualizations/Column/ColumnView';
import { DividerView } from '../../visualizations/Divider/DividerView';
import { TabsView } from '../../visualizations/Tabs/TabsView';
import { AccordionView } from '../../visualizations/Accordion/AccordionView';
import { TextView } from '../../visualizations/Text/TextView';
import { MetricView } from '../../visualizations/Metric/MetricView';
import { BadgeView } from '../../visualizations/Badge/BadgeView';
import { IconView } from '../../visualizations/Icon/IconView';
import { ImageView } from '../../visualizations/Image/ImageView';
import { ProgressView } from '../../visualizations/Progress/ProgressView';
import { CalloutView } from '../../visualizations/Callout/CalloutView';
import { LinkView } from '../../visualizations/Link/LinkView';
import { CodeView } from '../../visualizations/Code/CodeView';
import { MermaidView } from '../../visualizations/Mermaid/MermaidView';
import { BarChartView } from '../../visualizations/BarChart/BarChartView';
import { LineChartView } from '../../visualizations/LineChart/LineChartView';
import { AreaChartView } from '../../visualizations/AreaChart/AreaChartView';
import { PieChartView } from '../../visualizations/PieChart/PieChartView';
import { ScatterPlotView } from '../../visualizations/ScatterPlot/ScatterPlotView';
import { HeatmapView } from '../../visualizations/Heatmap/HeatmapView';
import { HistogramView } from '../../visualizations/Histogram/HistogramView';
import { RadarChartView } from '../../visualizations/RadarChart/RadarChartView';
import { SankeyChartView } from '../../visualizations/SankeyChart/SankeyChartView';
import { FunnelChartView } from '../../visualizations/FunnelChart/FunnelChartView';
import { GaugeChartView } from '../../visualizations/GaugeChart/GaugeChartView';
import { DataTableView } from '../../visualizations/DataTable/DataTableView';
import { KanbanView } from '../../visualizations/Kanban/KanbanView';
import { ListView } from '../../visualizations/List/ListView';
import { SparklineView } from '../../visualizations/Sparkline/SparklineView';
import { ButtonView } from '../../visualizations/Button/ButtonView';
import { CheckboxView } from '../../visualizations/Checkbox/CheckboxView';
import { ToggleView } from '../../visualizations/Toggle/ToggleView';
import { SelectView } from '../../visualizations/Select/SelectView';
import { TextFieldView } from '../../visualizations/TextField/TextFieldView';
import { SliderView } from '../../visualizations/Slider/SliderView';
import { CalendarView } from '../../visualizations/Calendar/CalendarView';
import { TimelineView } from '../../visualizations/Timeline/TimelineView';

// Each renderer has its own strict props type; the registry stores them
// behind a single RendererProps facade because SurfaceRenderer dispatches
// dynamically from JSON. The `as unknown as` bridge is the type-level
// admission that we trust validateSurfaceComponents (server-side Zod) to
// have enforced the right shape upstream.
type Renderer = ComponentType<RendererProps>;
const r = <T>(c: ComponentType<T>): Renderer => c as unknown as Renderer;

/**
 * Built-in renderers shipped with @deckspace/ui-components. Keyed by
 * VisualizationDefinition.type. Used as the default set in web-app's
 * catalog bootstrap.
 *
 * Data-backed renderers (DataTable, Kanban, List, Sparkline, Calendar,
 * Timeline, BarChart, LineChart, AreaChart, PieChart, ScatterPlot, Heatmap,
 * Histogram, RadarChart, SankeyChart, FunnelChart, GaugeChart) expect
 * `rows` + `columnDefs` as props. The web-app wraps them with the
 * `withDataset` HOC at registration time to fetch the dataset via TanStack
 * Query (see `apps/web-app/src/catalog-bootstrap.ts`).
 */
export const BUILTIN_RENDERERS: Record<string, Renderer> = {
  Row: r(RowView),
  Column: r(ColumnView),
  Divider: r(DividerView),
  Tabs: r(TabsView),
  Accordion: r(AccordionView),
  Text: r(TextView),
  Metric: r(MetricView),
  Badge: r(BadgeView),
  Icon: r(IconView),
  Mermaid: r(MermaidView),
  Image: r(ImageView),
  Progress: r(ProgressView),
  Callout: r(CalloutView),
  Link: r(LinkView),
  Code: r(CodeView),
  // 11 chart variants — replace the legacy meta-type 'Chart' entry
  BarChart: r(BarChartView),
  LineChart: r(LineChartView),
  AreaChart: r(AreaChartView),
  PieChart: r(PieChartView),
  ScatterPlot: r(ScatterPlotView),
  Heatmap: r(HeatmapView),
  Histogram: r(HistogramView),
  RadarChart: r(RadarChartView),
  SankeyChart: r(SankeyChartView),
  FunnelChart: r(FunnelChartView),
  GaugeChart: r(GaugeChartView),
  DataTable: r(DataTableView),
  Kanban: r(KanbanView),
  List: r(ListView),
  Sparkline: r(SparklineView),
  Button: r(ButtonView),
  Checkbox: r(CheckboxView),
  Toggle: r(ToggleView),
  Select: r(SelectView),
  TextField: r(TextFieldView),
  Slider: r(SliderView),
  Calendar: r(CalendarView),
  Timeline: r(TimelineView),
};
